import type { CreateActionPayload, ActionCreateResponse } from '@/contexts/actions/Actions.interfaces'
import {
  type RestAPIActionCriteria,
  type RestActionMeasure,
  RestApiActionCriteriaStatus,
  RestApiOwnerType,
  type RestAPIActionCreated,
  RestApiActionCriteriaTemplateType,
  type RestAPISubAction,
} from '@/interfaces/ApiRest.generated.interfaces'
import { ActionStatus } from '@/contexts/action-plans/ActionPlans.interfaces'
import { convertEnumValue } from '@/helpers/Enum.helpers'
import type { SimpleId, NullableId } from '@/contexts/security-base/SecurityBase.interfaces'

const buildActionMeasures = (
  perimeterId: number,
  beneficiaryIds: number[],
  solutionId: number,
  impact?: number
): RestActionMeasure[] => {
  const measures: RestActionMeasure[] = []
  const measureImpact = impact ?? 0

  measures.push({
    security_measure_id: solutionId,
    perimeter_id: perimeterId,
    impact: measureImpact,
  })

  beneficiaryIds.forEach((beneficiaryId) => {
    measures.push({
      security_measure_id: solutionId,
      perimeter_id: beneficiaryId,
      impact: measureImpact,
    })
  })

  return measures
}

export const createActionToApi = (payload: CreateActionPayload): RestAPIActionCriteria => {
  const measures = payload.solutionId
    ? buildActionMeasures(payload.perimeterId, payload.beneficiaryIds || [], payload.solutionId, payload.impact)
    : []

  const result: RestAPIActionCriteria = {
    identifier: payload.identifier,
    perimeter_id: payload.perimeterId,
    status: convertEnumValue(payload.status, RestApiActionCriteriaStatus) ?? RestApiActionCriteriaStatus.TO_PLAN,
    date_start: payload.dateStart,
    target_date: payload.targetDate,
    completion: payload.completion,
    name: payload.names
      ? {
          en: payload.names.en,
          fr: payload.names.fr,
        }
      : null,
    description: payload.descriptions
      ? {
          en: payload.descriptions.en,
          fr: payload.descriptions.fr,
        }
      : null,
    assigned_to: payload.owner
      ? {
          type: convertEnumValue(payload.owner.type, RestApiOwnerType)!,
          id: payload.owner.id,
        }
      : null,
    initial_cost: payload.initialCost,
    yearly_cost: payload.yearlyCost,
    init_workload: payload.initWorkload,
    yearly_workload: payload.yearlyWorkload,
    category_id: payload.categoryId,
    file_required: payload.fileRequired,
    priority: payload.priority,
    is_template: payload.isTemplate,
    forked_from_id: payload.forkedFromId,
    action_plans:
      payload.actionPlans?.map(({ id, name }) => ({
        id,
        name: {
          fr: name,
          en: name,
        },
      })) ?? [],
    associated_objects: {
      measures,
      labels: payload.associatedObjects?.labels || [],
      isp_projects: payload.associatedObjects?.ispProjects || [],
      incidents: payload.associatedObjects?.incidents || [],
      gaps: payload.associatedObjects?.gaps || [],
      exemptions: payload.associatedObjects?.exemptions || [],
      group_action:
        payload.associatedObjects?.actionRegisterGroups?.map(({ id, name, identifier }) => ({
          id,
          name,
          identifier,
        })) ?? [],
    },
    sub_action_items: payload.todoItems
      ? payload.todoItems.map(
          (item): RestAPISubAction => ({
            id: item.id,
            identifier: item.identifier,
            name: {
              en: item.names.en,
              fr: item.names.fr,
            },
            description: item.descriptions
              ? {
                  en: item.descriptions.en,
                  fr: item.descriptions.fr,
                }
              : null,
            proof_required: item.proofRequired,
            subaction_position: item.position,
            cost: item.cost,
            workload: item.workload,
            spent_cost: item.spentCost,
            spent_workload: item.spentWorkload,
            progress: item.progress,
            done: item.done,
            assigned_to: item.owner
              ? {
                  type: convertEnumValue(item.owner.type, RestApiOwnerType)!,
                  id: item.owner.id,
                }
              : null,
            link: item.link,
          })
        )
      : [],
  }

  if (payload.templateType) {
    result.template_type = convertEnumValue(payload.templateType, RestApiActionCriteriaTemplateType)
  }

  return result
}

export const actionCreateResponseFromApi = (action: RestAPIActionCreated): ActionCreateResponse => {
  const actionPlan: SimpleId | NullableId = action.action_plan ? { id: action.action_plan.id } : { id: null }

  return {
    id: action.id,
    identifier: action.identifier,
    perimeter_id: action.perimeter_id,
    name: action.name,
    status: convertEnumValue(action.status, ActionStatus) ?? ActionStatus.TO_PLAN,
    action_plan: actionPlan,
    completion: action.completion,
    impact: action.impact ?? 0,
    owner: undefined,
  }
}
